import { Platform } from 'react-native';

import { formatBusinessDate } from './date-utils';
import { formatMoneyCents } from './format-money';

export type PrintColumnFormat = 'money' | 'date' | 'integer' | 'text';

export interface PrintColumn<TRow> {
	header: string;
	accessor: (row: TRow) => unknown;
	format?: PrintColumnFormat;
	align?: 'left' | 'right' | 'center';
	/** Approximate column width in pixels; converted to a CSS width. */
	width?: number;
}

export interface PrintOptions<TRow> {
	/** Report title (used as the window title and `<h1>`). */
	title: string;
	/** Optional one-line subtitle under the title. */
	subtitle?: string;
	/** Optional association name displayed in the header strip. */
	associationName?: string;
	/** Optional summary line (e.g. "1,234 entries · range Jan 1 → Mar 31"). */
	summary?: string;
	columns: PrintColumn<TRow>[];
	rows: TRow[];
}

/**
 * Open a new browser window pre-formatted for printing a report. The window
 * is built with the DOM API (no `document.write`) so it satisfies our XSS
 * lint hook — all user-supplied strings flow through `textContent`, which
 * auto-escapes. `window.print()` is fired once layout settles.
 *
 * Native is a no-op (return false); callers should hide the Print affordance
 * on non-web platforms.
 */
export function openReportPrintWindow<TRow>(opts: PrintOptions<TRow>): boolean {
	if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
	const win = window.open('', '_blank');
	if (!win) return false;

	const doc = win.document;
	doc.title = opts.title;

	const style = doc.createElement('style');
	style.textContent = PRINT_CSS;
	doc.head.appendChild(style);

	const header = doc.createElement('header');
	if (opts.associationName) {
		const assoc = doc.createElement('div');
		assoc.className = 'rp-assoc';
		assoc.textContent = opts.associationName;
		header.appendChild(assoc);
	}
	const h1 = doc.createElement('h1');
	h1.textContent = opts.title;
	header.appendChild(h1);
	if (opts.subtitle) {
		const sub = doc.createElement('div');
		sub.className = 'rp-subtitle';
		sub.textContent = opts.subtitle;
		header.appendChild(sub);
	}
	if (opts.summary) {
		const sum = doc.createElement('div');
		sum.className = 'rp-summary';
		sum.textContent = opts.summary;
		header.appendChild(sum);
	}
	const stamp = doc.createElement('div');
	stamp.className = 'rp-stamp';
	stamp.textContent = `Printed ${new Date().toLocaleString()}`;
	header.appendChild(stamp);
	doc.body.appendChild(header);

	const table = doc.createElement('table');
	table.className = 'rp-table';

	if (opts.columns.some((c) => c.width)) {
		const colgroup = doc.createElement('colgroup');
		for (const col of opts.columns) {
			const colEl = doc.createElement('col');
			if (col.width) colEl.style.width = `${col.width}px`;
			colgroup.appendChild(colEl);
		}
		table.appendChild(colgroup);
	}

	const thead = doc.createElement('thead');
	const headRow = doc.createElement('tr');
	for (const col of opts.columns) {
		const th = doc.createElement('th');
		th.textContent = col.header;
		const align = alignFor(col);
		if (align !== 'left') th.style.textAlign = align;
		headRow.appendChild(th);
	}
	thead.appendChild(headRow);
	table.appendChild(thead);

	const tbody = doc.createElement('tbody');
	for (const row of opts.rows) {
		const tr = doc.createElement('tr');
		for (const col of opts.columns) {
			const td = doc.createElement('td');
			td.textContent = formatCell(col.accessor(row), col.format);
			const align = alignFor(col);
			if (align !== 'left') td.style.textAlign = align;
			tr.appendChild(td);
		}
		tbody.appendChild(tr);
	}
	table.appendChild(tbody);
	doc.body.appendChild(table);

	const footer = doc.createElement('footer');
	footer.className = 'rp-footer';
	footer.textContent = `${opts.rows.length.toLocaleString()} row${opts.rows.length === 1 ? '' : 's'}`;
	doc.body.appendChild(footer);

	// Give layout + fonts a moment to settle before invoking print, otherwise
	// Safari occasionally fires print() against an empty body.
	win.setTimeout(() => {
		try {
			win.focus();
			win.print();
		} catch {
			// User can still invoke Cmd+P manually if the auto-print is blocked.
		}
	}, 250);

	return true;
}

function alignFor<TRow>(col: PrintColumn<TRow>): 'left' | 'right' | 'center' {
	if (col.align) return col.align;
	if (col.format === 'money' || col.format === 'integer') return 'right';
	return 'left';
}

function formatCell(v: unknown, format: PrintColumnFormat | undefined): string {
	if (v == null || v === '') return '';
	if (format === 'money' && typeof v === 'number') return formatMoneyCents(v);
	if (format === 'date' && typeof v === 'number') return formatBusinessDate(v);
	if (format === 'integer' && typeof v === 'number') return v.toLocaleString();
	if (typeof v === 'number') return String(v);
	if (typeof v === 'string') return v;
	if (typeof v === 'boolean') return v ? 'yes' : '';
	return String(v);
}

const PRINT_CSS = `
	@page { size: letter landscape; margin: 0.5in; }
	* { box-sizing: border-box; }
	body {
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		color: #212121;
		margin: 0;
		padding: 16px;
		font-size: 11px;
	}
	header { margin-bottom: 12px; }
	.rp-assoc {
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.6px;
		color: #546e7a;
		font-weight: 700;
	}
	h1 {
		font-size: 18px;
		margin: 2px 0 4px;
		color: #102542;
	}
	.rp-subtitle {
		font-size: 11px;
		color: #455a64;
		margin-bottom: 2px;
	}
	.rp-summary {
		font-size: 11px;
		color: #37474f;
	}
	.rp-stamp {
		font-size: 10px;
		color: #90a4ae;
		margin-top: 4px;
	}
	table.rp-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 10.5px;
	}
	table.rp-table th,
	table.rp-table td {
		padding: 4px 6px;
		border-bottom: 1px solid #e0e7ef;
		vertical-align: top;
		text-align: left;
	}
	table.rp-table thead th {
		font-weight: 700;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 0.4px;
		color: #37474f;
		background: #eceff1;
		border-bottom: 2px solid #b0bec5;
	}
	table.rp-table tbody tr {
		page-break-inside: avoid;
	}
	table.rp-table tbody tr:nth-child(even) td {
		background: #f7f9fb;
	}
	thead { display: table-header-group; }
	tfoot { display: table-footer-group; }
	.rp-footer {
		margin-top: 12px;
		font-size: 10px;
		color: #90a4ae;
		text-align: right;
	}
	@media print {
		body { padding: 0; }
		.rp-stamp { display: none; }
	}
`;
